import { Field, TextInput, SelectField, TextAreaField } from "./FormFields";
import { PHOTO_PRIVACY, CONTACT_METHODS, RELATIONSHIP_OPTIONS } from "@/lib/profileForm";
import { fileToCompressedDataUrl } from "@/lib/image";
import { calculateAge, formatDOB } from "@/lib/profiles";
import { useLanguage } from "@/lib/i18n";

const MAX_SIZE = 5 * 1024 * 1024;

export const StepPhotos = ({ data, set, errors }) => {
  const onMain = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      set("mainPhotoError", "Please choose an image file (JPG or PNG).");
      return;
    }
    if (file.size > MAX_SIZE) {
      set("mainPhotoError", "Please choose an image smaller than 5 MB.");
      return;
    }
    set("mainPhotoError", "");
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      set("mainPhoto", null);
      set("mainPhotoUrl", dataUrl);
    } catch (err) {
      set("mainPhotoError", "Could not process this image. Please try another photo.");
    }
  };

  const onAdditional = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    const urls = [];
    for (const f of files.slice(0, 4)) {
      try {
        urls.push(await fileToCompressedDataUrl(f));
      } catch (err) {
        /* skip unreadable file */
      }
    }
    set("additionalPhotos", [...data.additionalPhotos, ...urls].slice(0, 4));
  };

  return (
    <div className="cp-grid cp-grid-1">
      <Field label="Main Profile Photo" required error={errors.mainPhoto || data.mainPhotoError}>
        {data.mainPhotoUrl ? (
          <div className="cp-upload-preview" data-testid="cp-main-preview">
            <img src={data.mainPhotoUrl} alt="Main profile preview" />
            <div className="cp-upload-preview-actions">
              <label className="cp-upload-btn" htmlFor="cp-main-photo">
                Change Photo
                <input
                  id="cp-main-photo"
                  type="file"
                  accept="image/*"
                  onChange={onMain}
                />
              </label>
              <button
                type="button"
                className="cp-upload-remove"
                data-testid="cp-main-remove"
                onClick={() => {
                  set("mainPhoto", null);
                  set("mainPhotoUrl", "");
                }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <label className="cp-upload" htmlFor="cp-main-photo" data-testid="cp-main-upload">
            <input id="cp-main-photo" type="file" accept="image/*" onChange={onMain} />
            <span className="cp-upload-title">Upload Main Photo</span>
            <span className="cp-upload-sub">JPG or PNG, up to 5 MB — a modest, clear photo works best.</span>
          </label>
        )}
      </Field>

      <Field label="Additional Photos" hint="Up to 4 extra photos.">
        <label className="cp-upload cp-upload-sm" htmlFor="cp-add-photos" data-testid="cp-additional-upload">
          <input
            id="cp-add-photos"
            type="file"
            accept="image/*"
            multiple
            onChange={onAdditional}
          />
          <span className="cp-upload-title">Add Photos</span>
        </label>
        {data.additionalPhotos.length > 0 && (
          <div className="cp-thumbs" data-testid="cp-thumbs">
            {data.additionalPhotos.map((url, i) => (
              <span key={url} className="cp-thumb">
                <img src={url} alt={`Additional ${i + 1}`} />
                <button
                  type="button"
                  aria-label={`Remove photo ${i + 1}`}
                  data-testid={`cp-thumb-remove-${i}`}
                  onClick={() =>
                    set(
                      "additionalPhotos",
                      data.additionalPhotos.filter((u) => u !== url),
                    )
                  }
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </Field>

      <SelectField
        label="Photo Privacy"
        required
        id="cp-photo-privacy"
        options={PHOTO_PRIVACY}
        value={data.photoPrivacy}
        onChange={(v) => set("photoPrivacy", v)}
        error={errors.photoPrivacy}
      />

      <p className="cp-privacy-note" data-testid="cp-photos-note">
        Your contact information is never publicly displayed — only your photo follows the privacy
        choice above.
      </p>
    </div>
  );
};

export const StepContact = ({ data, set, errors }) => (
  <div className="cp-grid">
    <TextInput
      label="Mobile Number"
      required
      id="cp-mobile"
      type="tel"
      placeholder="Mobile number"
      autoComplete="tel"
      hint="Private — never shown on your public profile."
      value={data.mobile}
      onChange={(e) => set("mobile", e.target.value)}
      error={errors.mobile}
    />
    <TextInput
      label="Email Address"
      id="cp-email"
      type="email"
      placeholder="you@example.com"
      autoComplete="email"
      hint="Private — never shown on your public profile."
      value={data.email}
      onChange={(e) => set("email", e.target.value)}
      error={errors.email}
    />
    <TextInput
      label="WhatsApp Number"
      id="cp-whatsapp"
      type="tel"
      placeholder="WhatsApp number (if different)"
      hint="Private — never shown on your public profile."
      value={data.whatsapp}
      onChange={(e) => set("whatsapp", e.target.value)}
      error={errors.whatsapp}
    />
    <SelectField
      label="Preferred Contact Method"
      id="cp-contact-method"
      options={CONTACT_METHODS}
      value={data.preferredContact}
      onChange={(v) => set("preferredContact", v)}
      error={errors.preferredContact}
    />
    <TextInput
      label="Parent / Wali Name"
      required
      id="cp-wali-name"
      placeholder="Enter Parent / Wali name"
      hint="Private — never shown on your public profile."
      value={data.waliName}
      onChange={(e) => set("waliName", e.target.value)}
      error={errors.waliName}
    />
    <SelectField
      label="Relationship"
      required
      id="cp-wali-relationship"
      options={RELATIONSHIP_OPTIONS}
      value={data.waliRelationship}
      onChange={(v) => set("waliRelationship", v)}
      error={errors.waliRelationship}
    />
    <TextInput
      label="Parent / Wali Mobile Number"
      required
      id="cp-wali-mobile"
      type="tel"
      placeholder="Parent / Wali mobile number"
      hint="Private — never shown on your public profile."
      value={data.waliMobile}
      onChange={(e) => set("waliMobile", e.target.value)}
      error={errors.waliMobile}
    />
    <div className="cp-grid-1">
      <TextAreaField
        label="Full Current Address"
        required
        id="cp-full-address"
        rows={5}
        placeholder="Enter your complete current address"
        hint="Private — never shown on your public profile."
        value={data.fullAddress}
        onChange={(v) => set("fullAddress", v)}
        error={errors.fullAddress}
        testId="cp-full-address"
      />
    </div>
    <p className="cp-privacy-note" data-testid="cp-contact-note">
      Your mobile number and email are private by default. They are never displayed publicly and
      are only used through the appropriate Suffa Matrimony contact process.
    </p>
  </div>
);

const Row = ({ label, value, chip }) => {
  const { t } = useLanguage();
  return (
    <div className="cp-preview-row">
      <span className="cp-preview-label">{t(label)}</span>
      <span className="cp-preview-value">
        {value || "—"}
        {chip && <span className="cp-private-chip">{t(chip)}</span>}
      </span>
    </div>
  );
};

export const ProfilePreview = ({ data, onEdit, stepTitleMap }) => {
  const age = calculateAge(data.dob);
  return (
    <div data-testid="cp-preview">
      <div className="cp-preview-hero">
        {data.mainPhotoUrl ? (
          <img src={data.mainPhotoUrl} alt="Main profile" className="cp-preview-photo" />
        ) : (
          <span className="fm-medallion" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 40 40" fill="none">
              <g stroke="#C5A059" strokeWidth="1.5" fill="none">
                <rect x="12.5" y="12.5" width="15" height="15" />
                <rect x="12.5" y="12.5" width="15" height="15" transform="rotate(45 20 20)" />
              </g>
              <circle cx="20" cy="20" r="2.6" fill="#C5A059" />
            </svg>
          </span>
        )}
        <div className="cp-preview-intro">
          <span className={`fm-chip fm-chip-${data.gender === "Male" ? "groom" : "bride"}`}>
            {data.gender === "Male" ? "Groom" : "Bride"}
          </span>
          <h3 className="cp-preview-name">{data.fullName}</h3>
          <p className="cp-preview-meta">
            {data.city}, {data.country} · Age: {age}
          </p>
        </div>
      </div>

      <Section title="Basic Information" step={2} onEdit={onEdit} stepTitleMap={stepTitleMap}>
        <Row label="Date of Birth" value={formatDOB(data.dob)} chip={`Shown publicly as Age ${age}`} />
        <Row label="Marital Status" value={data.maritalStatus} />
        {data.maritalStatus && data.maritalStatus !== "Never Married" && (
          <Row label="Children" value={data.children} />
        )}
        <Row label="Nationality" value={data.nationality} />
        {data.communityJamaat && (
          <Row
            label="Community / Jamaat"
            value={
              data.communityJamaat === "Other" ? data.communityJamaatOther : data.communityJamaat
            }
          />
        )}
        <Row label="Location" value={[data.city, data.state, data.country].filter(Boolean).join(", ")} />
      </Section>

      <Section title="Education & Career" step={3} onEdit={onEdit} stepTitleMap={stepTitleMap}>
        <Row
          label="School Education"
          value={data.schoolEducation === "Other" ? data.schoolEducationOther : data.schoolEducation}
        />
        <Row
          label="Madarsa Education"
          value={
            data.madarsaEducation === "Other"
              ? data.madarsaEducationOther
              : data.madarsaEducation
          }
        />
        <Row
          label="Profession"
          value={data.profession === "Other" ? data.professionOther : data.profession}
        />
        {data.income && (
          <Row label="Income" value={data.income} chip="Private — not publicly displayed" />
        )}
      </Section>

      {(data.religiousBackgrounds?.length || data.schoolOfThought?.length || data.religiousPractice) && (
        <Section title="Religious Information" step={4} onEdit={onEdit} stepTitleMap={stepTitleMap}>
          {data.religiousBackgrounds?.length > 0 && (
            <Row label="Religious Background" value={data.religiousBackgrounds.join(", ")} />
          )}
          {data.schoolOfThought?.length > 0 && (
            <Row
              label="School of Thought / Religious Affiliation"
              value={
                data.schoolOfThought.includes("Other")
                  ? [
                      ...data.schoolOfThought.filter((o) => o !== "Other"),
                      data.schoolOfThoughtOther,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : data.schoolOfThought.join(", ")
              }
            />
          )}
          {data.religiousPractice && (
            <Row
              label="Religious Practice"
              value={
                data.religiousPractice === "Other"
                  ? data.religiousPracticeOther
                  : data.religiousPractice
              }
            />
          )}
        </Section>
      )}

      <Section title="Lifestyle" step={5} onEdit={onEdit} stepTitleMap={stepTitleMap}>
        {data.height && <Row label="Height" value={`${data.height} cm`} />}
        {data.weight && <Row label="Weight" value={`${data.weight} kg`} />}
        {data.complexion && (
          <Row
            label="Complexion / Skin Tone"
            value={data.complexion === "Other" ? data.complexionOther : data.complexion}
          />
        )}
        {data.diet && <Row label="Diet" value={data.diet} />}
        {data.smoking && <Row label="Smoking" value={data.smoking} />}
        {data.tobacco && <Row label="Tobacco" value={data.tobacco} />}
        {data.alcohol && <Row label="Alcohol" value={data.alcohol} />}
        {data.languages?.length > 0 && (
          <Row
            label="Languages"
            value={
              data.languages.includes("Other")
                ? [
                    ...data.languages.filter((o) => o !== "Other"),
                    data.languagesOther,
                  ]
                    .filter(Boolean)
                    .join(", ")
                : data.languages.join(", ")
            }
          />
        )}
        {data.hobbies && <Row label="Hobbies & Interests" value={data.hobbies} />}
      </Section>

      <Section title="Family" step={6} onEdit={onEdit} stepTitleMap={stepTitleMap}>
        {data.familyType && <Row label="Family Type" value={data.familyType} />}
        {data.familyValues && <Row label="Family Values" value={data.familyValues} />}
        {data.fatherOccupation && <Row label="Father’s Occupation" value={data.fatherOccupation} />}
        {data.motherOccupation && <Row label="Mother’s Occupation" value={data.motherOccupation} />}
        {data.siblings && <Row label="Siblings" value={data.siblings} />}
        {data.familyLocation && <Row label="Family Location" value={data.familyLocation} />}
        {data.familyIntro && <p className="cp-preview-text">{data.familyIntro}</p>}
      </Section>

      <Section title="Marriage Preferences" step={7} onEdit={onEdit} stepTitleMap={stepTitleMap}>
        {(data.prefAgeFrom || data.prefAgeTo) && (
          <Row label="Preferred Age" value={[data.prefAgeFrom, data.prefAgeTo].filter(Boolean).join(" – ")} />
        )}
        {data.prefLocations?.length > 0 && (
          <Row label="Preferred Country / Location" value={data.prefLocations.join(", ")} />
        )}
        {data.prefMaritalStatuses?.length > 0 && (
          <Row label="Preferred Marital Status" value={data.prefMaritalStatuses.join(", ")} />
        )}
        {data.prefEducation && <Row label="Preferred Education" value={data.prefEducation} />}
        {data.prefProfession && <Row label="Preferred Profession" value={data.prefProfession} />}
        {data.prefReligious && <Row label="Religious Preferences" value={data.prefReligious} />}
        {data.prefHeight && <Row label="Preferred Height" value={data.prefHeight} />}
        {data.prefChildren && <Row label="Children Preference" value={data.prefChildren} />}
        {data.partnerVision && <p className="cp-preview-text">{data.partnerVision}</p>}
      </Section>

      <Section title="Contact & Privacy" step={9} onEdit={onEdit} stepTitleMap={stepTitleMap}>
        <Row label="Mobile Number" value={data.mobile} chip="Private — not publicly displayed" />
        <Row label="Email Address" value={data.email} chip="Private — not publicly displayed" />
        {data.whatsapp && <Row label="WhatsApp Number" value={data.whatsapp} chip="Private — not publicly displayed" />}
        {data.preferredContact && <Row label="Preferred Contact Method" value={data.preferredContact} />}
        {data.waliName && <Row label="Parent / Wali Name" value={data.waliName} chip="Private — not publicly displayed" />}
        {data.waliRelationship && <Row label="Relationship" value={data.waliRelationship} chip="Private — not publicly displayed" />}
        {data.waliMobile && <Row label="Parent / Wali Mobile Number" value={data.waliMobile} chip="Private — not publicly displayed" />}
      </Section>
    </div>
  );
};

const Section = ({ title, step, onEdit, stepTitleMap, children }) => {
  const { t } = useLanguage();
  return (
    <section className="cp-section">
      <div className="cp-section-head">
        <h4>{t(title)}</h4>
        <button
          type="button"
          className="cp-edit-btn"
          data-testid={`cp-edit-${step}`}
          onClick={() => onEdit(step)}
        >
          Edit
        </button>
      </div>
      <div className="cp-section-body">{children}</div>
    </section>
  );
};
